import React, { useState, useEffect } from 'react';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  Divider,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface EInvoiceIntegrationData {
  id?: number;
  username: string;
  password: string;
  companyName: string;
  taxNumber: string;
  providerType: 'finansbank' | 'edm';
  isActive: boolean;
  /** E-fatura seri öneki (entegratör hesabında kayıtlı, ör: "ABC"). Boş → otomatik çözülür. */
  eInvoiceSerialPrefix?: string;
  /** E-arşiv seri öneki (ör: "ABD"). Boş → otomatik çözülür. */
  eArsivSerialPrefix?: string;
  /** Gönderici birim (GB) etiketi (ör: urn:mail:defaultgb@sirket.com.tr). Boş → otomatik çözülür. */
  senderAlias?: string;
  /** Ortam: false → TEST, true → CANLI (production). Kayıtta hiç seçilmemişse sunucu konfigi geçerlidir. */
  isProduction?: boolean;
  /** Faturada gösterilecek banka hesabı ID'leri (JSON int dizisi, ör: "[3,7]"). Boş → banka bloğu yazılmaz. */
  invoiceBankAccountIds?: string;
}

/** EDM hesabında kayıtlı seri kaydı (edm/serials yanıtı) */
interface EdmSerialItem {
  code: string;
  year: number;
  sendType?: string;
  lastSerialUsed?: string | number;
  isArchive: boolean;
}

/** Hesabın GİB'de kayıtlı GB (gönderici birim) etiketi (edm/gb-aliases yanıtı) */
interface EdmGbAliasItem {
  alias: string;
  title?: string;
  creationTime?: string;
  isActive: boolean;
}

interface EInvoiceIntegrationModalProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  companyId: string | null;
}

type FormErrors = {
  [K in keyof Omit<EInvoiceIntegrationData, 'id' | 'isActive' | 'eInvoiceSerialPrefix' | 'eArsivSerialPrefix' | 'senderAlias' | 'invoiceBankAccountIds'>]?: boolean;
};

const initialIntegrationData: Omit<EInvoiceIntegrationData, 'id'> = {
  username: '',
  password: '',
  companyName: '',
  taxNumber: '',
  providerType: 'finansbank',
  isActive: false,
  eInvoiceSerialPrefix: '',
  eArsivSerialPrefix: '',
  senderAlias: '',
  isProduction: false,
  invoiceBankAccountIds: '',
};

const EInvoiceIntegrationModal: React.FC<EInvoiceIntegrationModalProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // Tab state
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0); // 0: Finansbank, 1: EDM, 2: PavoPay
  
  // Entegrasyon verileri
  const [finansbankData, setFinansbankData] = useState<EInvoiceIntegrationData>({ ...initialIntegrationData, providerType: 'finansbank' });
  const [edmData, setEdmData] = useState<EInvoiceIntegrationData>({ ...initialIntegrationData, providerType: 'edm' });
  
  // PavoPay state
  const [pavoPayUsername, setPavoPayUsername] = useState('');
  const [pavoPayPassword, setPavoPayPassword] = useState('');
  const [pavoPayBranchId, setPavoPayBranchId] = useState<string>('');
  const [pavoPayBranchName, setPavoPayBranchName] = useState('');
  const [showPavoPayPassword, setShowPavoPayPassword] = useState(false);
  const [pavoPaySaving, setPavoPaySaving] = useState(false);
  const [pavoPayTesting, setPavoPayTesting] = useState(false);
  const [pavoPayIntegrations, setPavoPayIntegrations] = useState<any[]>([]);
  const [pavoPayLoading, setPavoPayLoading] = useState(false);

  // UI state'leri
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPasswordFinansbank, setShowPasswordFinansbank] = useState(false);
  const [showPasswordEdm, setShowPasswordEdm] = useState(false);
  const [companyDetailsLoaded, setCompanyDetailsLoaded] = useState(false);
  
  // Placeholder şifre kontrolü için
  const [finansbankOriginalPassword, setFinansbankOriginalPassword] = useState<string>('');
  const [edmOriginalPassword, setEdmOriginalPassword] = useState<string>('');

  // EDM bağlantı testi (yeni gts_einvoice_api üzerinden — credential SQL'den çözülür)
  const [edmTesting, setEdmTesting] = useState(false);
  const [edmTestResult, setEdmTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // EDM'de kayıtlı fatura serileri (Serileri Getir)
  const [edmSerials, setEdmSerials] = useState<EdmSerialItem[] | null>(null);
  const [edmSerialsLoading, setEdmSerialsLoading] = useState(false);
  const [edmSerialsError, setEdmSerialsError] = useState<string | null>(null);

  // Hesabın GİB'de kayıtlı GB (gönderici birim) etiketleri (Etiketleri Getir)
  const [edmGbAliases, setEdmGbAliases] = useState<EdmGbAliasItem[] | null>(null);
  const [edmGbAliasesLoading, setEdmGbAliasesLoading] = useState(false);
  const [edmGbAliasesError, setEdmGbAliasesError] = useState<string | null>(null);

  // Faturada gösterilecek banka hesapları (UBL PaymentMeans + XSLT banka kutusu)
  const [bankAccounts, setBankAccounts] = useState<{ id: number; bankName: string; branchName?: string; iban?: string }[] | null>(null);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);

  // EDM mükellef sorgu (CheckUser) — VKN/TCKN e-fatura mükellefi mi?
  const [edmQueryVkn, setEdmQueryVkn] = useState('');
  const [edmQuerying, setEdmQuerying] = useState(false);
  const [edmQueryResult, setEdmQueryResult] = useState<{
    ok: boolean;
    message: string;
    isEInvoiceUser?: boolean;
    title?: string;
    users?: Array<{ alias?: string; unit?: string; documentType?: string; title?: string; aliasCreationTime?: string }>;
  } | null>(null);
  
  const { userDetails, branches, loadBranches } = useAuth();

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && companyId) {
      loadCompanyDetails();
      loadAllIntegrations();
      loadPavoPayIntegrations();
      loadBranches();
      loadBankAccounts();
    }
  }, [open, companyId]);

  const loadCompanyDetails = async () => {
    try {
      // Şirket bilgilerini SQL'den çek (GET /api/Company)
      const companyData = await getCompanyInfo(companyId!);

      if (companyData) {
        const companyName = companyData.companyName || '';
        const taxNumber = companyData.taxNumber || '';

        setFinansbankData(prev => ({ ...prev, companyName, taxNumber }));
        setEdmData(prev => ({ ...prev, companyName, taxNumber }));
        setCompanyDetailsLoaded(true);
      } else {
        setError(tt('Şirket bilgileri bulunamadı. Lütfen önce firma bilgilerinizi tamamlayın.', 'Company information not found. Please complete your company details first.'));
      }
    } catch (err) {
      console.error("Şirket bilgileri yüklenirken hata:", err);
      setError(tt("Şirket bilgileri yüklenirken bir hata oluştu.", "An error occurred while loading company details."));
    }
  };

  const loadAllIntegrations = async () => {
    setLoading(true);
    try {
      console.log('📥 [E-INVOICE INTEGRATION] Tüm entegrasyonlar yükleniyor...', { companyId });

      // ✅ SQL API'den tüm entegrasyonları çek
      const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_LIST(false));
      const result = response.data;
      console.log('📦 [E-INVOICE INTEGRATION] Raw response:', result);
      
      const integrations = result.data || [];
      console.log('✅ [E-INVOICE INTEGRATION] Entegrasyonlar yüklendi:', integrations.length, 'adet', integrations);

      // Finansbank entegrasyonunu bul
      const finansbank = integrations.find((i: any) => i.providerType === 'finansbank' || i.ProviderType === 'finansbank');
      console.log('🏦 [FINANSBANK] Bulunan data:', finansbank);
      
      if (finansbank) {
        setFinansbankData(prev => {
          const fbData = {
            id: finansbank.id || finansbank.Id,
            username: finansbank.username || finansbank.Username || '',
            password: '••••••••••••', // Placeholder
            // VKN/ünvan şirket bilgisinden (salt-okunur, tek doğruluk kaynağı) gelir;
            // kaydın eski snapshot'ı KULLANILMAZ. Şirket düzeltilince Kaydet ile güncellenir.
            companyName: prev.companyName || finansbank.companyName || finansbank.CompanyName || '',
            taxNumber: prev.taxNumber || finansbank.taxNumber || finansbank.TaxNumber || '',
            providerType: 'finansbank' as const,
            isActive: finansbank.isActive || finansbank.IsActive || false,
          };
          console.log('🏦 [FINANSBANK] Set edilen data:', fbData);
          return fbData;
        });
        setFinansbankOriginalPassword(finansbank.password || finansbank.Password || '');
      }

      // EDM entegrasyonunu bul
      const edm = integrations.find((i: any) => i.providerType === 'edm' || i.ProviderType === 'edm');
      console.log('📡 [EDM] Bulunan data:', edm);
      
      if (edm) {
        setEdmData(prev => {
          const edmDataObj = {
            id: edm.id || edm.Id,
            username: edm.username || edm.Username || '',
            password: '••••••••••••', // Placeholder
            // VKN/ünvan şirket bilgisinden (salt-okunur, tek doğruluk kaynağı) gelir;
            // kaydın eski snapshot'ı KULLANILMAZ. Şirket düzeltilince Kaydet ile güncellenir.
            companyName: prev.companyName || edm.companyName || edm.CompanyName || '',
            taxNumber: prev.taxNumber || edm.taxNumber || edm.TaxNumber || '',
            providerType: 'edm' as const,
            isActive: edm.isActive || edm.IsActive || false,
            eInvoiceSerialPrefix: edm.eInvoiceSerialPrefix || edm.EInvoiceSerialPrefix || '',
            eArsivSerialPrefix: edm.eArsivSerialPrefix || edm.EArsivSerialPrefix || '',
            senderAlias: edm.senderAlias || edm.SenderAlias || '',
            isProduction: (edm.isProduction ?? edm.IsProduction ?? false) === true,
            invoiceBankAccountIds: edm.invoiceBankAccountIds || edm.InvoiceBankAccountIds || '',
          };
          console.log('📡 [EDM] Set edilen data:', edmDataObj);
          return edmDataObj;
        });
        setEdmOriginalPassword(edm.password || edm.Password || '');
      }

    } catch (err) {
      console.error("❌ [E-INVOICE INTEGRATION] Entegrasyonlar yüklenirken hata:", err);
      setError(tt('Entegrasyonlar yüklenirken hata oluştu. Konsolu kontrol edin.', 'An error occurred while loading integrations. Check console.'));
    } finally {
      setLoading(false);
    }
  };

  const loadPavoPayIntegrations = async () => {
    setPavoPayLoading(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
      const result = response.data;
      setPavoPayIntegrations(result.data || []);
    } catch (err) {
      console.error('[PavoPay] Entegrasyonlar yüklenirken hata:', err);
    } finally {
      setPavoPayLoading(false);
    }
  };

  const handlePavoPaySave = async () => {
    setError(null);
    if (!pavoPayUsername.trim()) {
      setError(tt('Kullanıcı adı zorunludur.', 'Username is required.'));
      return;
    }
    if (!pavoPayPassword.trim()) {
      setError(tt('Şifre zorunludur.', 'Password is required.'));
      return;
    }

    setPavoPaySaving(true);
    try {
      await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_SAVE, {
        Username: pavoPayUsername.trim(),
        EncryptedPassword: pavoPayPassword.trim(),
        BranchId: pavoPayBranchId && pavoPayBranchId !== 'null' ? pavoPayBranchId : null,
        BranchName: pavoPayBranchName.trim() || null,
        UpdatedBy: userDetails?.email || 'Unknown'
      });
      setSuccess(tt('PavoPay entegrasyonu başarıyla kaydedildi!', 'PavoPay integration saved successfully!'));
      setPavoPayUsername('');
      setPavoPayPassword('');
      setPavoPayBranchId('');
      setPavoPayBranchName('');
      await loadPavoPayIntegrations();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('[PavoPay] Kaydetme hatası:', err);
      setError(handleApiError(err));
    } finally {
      setPavoPaySaving(false);
    }
  };

  const handlePavoPayAuthTest = async (integrationId: number) => {
    setError(null);
    setPavoPayTesting(true);
    try {
      const response = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_AUTH_TEST, {
        Id: integrationId
      });
      const result = response.data;
      if (result.success) {
        setSuccess(tt('Bağlantı testi başarılı!', 'Connection test successful!'));
        await loadPavoPayIntegrations();
      } else {
        setError(result.message || tt('Bağlantı testi başarısız.', 'Connection test failed.'));
      }
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('[PavoPay] Auth test hatası:', err);
      setError(handleApiError(err));
    } finally {
      setPavoPayTesting(false);
    }
  };

  const handlePavoPayDelete = async (integrationId: number) => {
    if (!window.confirm(tt('Bu PavoPay entegrasyonunu silmek istediğinizden emin misiniz?', 'Are you sure you want to delete this PavoPay integration?'))) return;
    try {
      setPavoPaySaving(true);
      await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_DELETE, { Id: integrationId });
      setSuccess(tt('PavoPay entegrasyonu silindi.', 'PavoPay integration deleted.'));
      await loadPavoPayIntegrations();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setPavoPaySaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue as 0 | 1 | 2);
    setError(null);
    setFormErrors({});
    setEdmTestResult(null);
    setEdmQueryResult(null);
    setEdmSerials(null);
    setEdmSerialsError(null);
  };

  const handleChange = (provider: 'finansbank' | 'edm') => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (provider === 'finansbank') {
      setFinansbankData(prev => ({ ...prev, [name]: value }));
    } else {
      setEdmData(prev => ({ ...prev, [name]: value }));
    }
    
    const fieldName = name as keyof FormErrors;
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({ ...prev, [fieldName]: false }));
    }
    
    if (error) {
      setError(null);
    }
  };

  const togglePasswordVisibility = (provider: 'finansbank' | 'edm') => () => {
    if (provider === 'finansbank') {
      const newShowState = !showPasswordFinansbank;
      setShowPasswordFinansbank(newShowState);
      
      // Şifre placeholder ise gerçek şifreyi göster
      if (newShowState && finansbankData.password === '••••••••••••' && finansbankOriginalPassword) {
        setFinansbankData(prev => ({ ...prev, password: finansbankOriginalPassword }));
      } else if (!newShowState && finansbankData.password === finansbankOriginalPassword) {
        setFinansbankData(prev => ({ ...prev, password: '••••••••••••' }));
      }
    } else {
      const newShowState = !showPasswordEdm;
      setShowPasswordEdm(newShowState);
      
      if (newShowState && edmData.password === '••••••••••••' && edmOriginalPassword) {
        setEdmData(prev => ({ ...prev, password: edmOriginalPassword }));
      } else if (!newShowState && edmData.password === edmOriginalPassword) {
        setEdmData(prev => ({ ...prev, password: '••••••••••••' }));
      }
    }
  };

  const handleToggleActive = async (provider: 'finansbank' | 'edm') => {
    const data = provider === 'finansbank' ? finansbankData : edmData;
    
    if (!data.id) {
      setError(tt('Bu entegrasyon henüz kaydedilmemiş. Önce kaydedin.', 'This integration is not saved yet. Save it first.'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const requestBody = {
        CompanyId: companyId!,
        IntegrationId: data.id,
        IsActive: !data.isActive,
        UpdatedBy: userDetails?.email || 'Unknown'
      };

      await accountingApi.post(ACCOUNTING_API.EINVOICE_INTEGRATION_TOGGLE_ACTIVE, requestBody);

      setSuccess(
        isTr
          ? `${provider === 'finansbank' ? 'Finansbank' : 'EDM'} entegrasyonu ${!data.isActive ? 'aktif' : 'pasif'} edildi.`
          : `${provider === 'finansbank' ? 'Finansbank' : 'EDM'} integration ${!data.isActive ? 'activated' : 'deactivated'}.`
      );
      
      // Verileri yeniden yükle
      await loadAllIntegrations();

      setTimeout(() => {
        setSuccess(null);
      }, 2000);

    } catch (err) {
      console.error('❌ [E-INVOICE INTEGRATION] Toggle aktif hatası:', err);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setFormErrors({});
    setError(null);

    const currentData = activeTab === 0 ? finansbankData : edmData;
    const originalPassword = activeTab === 0 ? finansbankOriginalPassword : edmOriginalPassword;

    const requiredFields: Array<keyof Omit<EInvoiceIntegrationData, 'id' | 'isActive'>> = [
      'username', 
      'password', 
      'companyName', 
      'taxNumber'
    ];
    
    const newErrors: FormErrors = {};
    let hasError = false;

    requiredFields.forEach(field => {
      // Şifre alanı için özel kontrol: eğer mevcut entegrasyon varsa ve placeholder gösteriliyorsa geçerli say
      if (field === 'password' && currentData.id && currentData[field] === '••••••••••••') {
        // Mevcut şifre korunuyor, zorunlu alan kontrolünü atla
        return;
      }
      
      if (!currentData[field]) {
        newErrors[field] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setFormErrors(newErrors);
      setError(tt("Lütfen tüm zorunlu alanları (*) doldurunuz.", "Please fill all required fields (*)."));
      return;
    }

    if (!companyId) {
      setError(tt("Şirket ID bulunamadı, kayıt yapılamıyor.", "Company ID not found, cannot save."));
      return;
    }

    setSaving(true);
    
    try {
      let passwordToSave = currentData.password;
      
      // Eğer şifre placeholder ise (••••••••••••) ve mevcut entegrasyon varsa, mevcut şifreyi koru
      if (currentData.id && currentData.password === '••••••••••••') {
        passwordToSave = originalPassword;
        console.log('Mevcut şifre korunuyor');
      } else if (currentData.id && currentData.password === originalPassword) {
        passwordToSave = originalPassword;
        console.log('Görüntülenen mevcut şifre korunuyor');
      } else {
        console.log('Yeni şifre kaydediliyor');
      }

      console.log('💾 [E-INVOICE INTEGRATION] SQL\'e kaydediliyor...', { companyId, providerType: currentData.providerType });

      // ✅ SQL API'ye plain text veriyi kaydet (backend'de şifrelenecek)
      const requestBody = {
        companyId: companyId,
        username: currentData.username,
        password: passwordToSave, // Plain text password (HTTPS ile güvenli, backend'de şifrelenecek)
        companyName: currentData.companyName,
        taxNumber: currentData.taxNumber,
        providerType: currentData.providerType,
        eInvoiceSerialPrefix: currentData.eInvoiceSerialPrefix?.trim() || null,
        eArsivSerialPrefix: currentData.eArsivSerialPrefix?.trim() || null,
        senderAlias: currentData.senderAlias?.trim() || null,
        // Ortam her kayıtta açıkça gönderilir (test/canlı UI'dan yönetilir)
        isProduction: currentData.isProduction === true,
        // Banka seçimi her kayıtta açıkça gönderilir ('' → temizle; backend null'ı "değiştirme" sayar)
        invoiceBankAccountIds: currentData.invoiceBankAccountIds?.trim() || '',
        createdBy: userDetails?.email || 'Unknown',
        updatedBy: userDetails?.email || 'Unknown'
      };

      const response = await accountingApi.post(ACCOUNTING_API.EINVOICE_INTEGRATION_SAVE, requestBody);
      const result = response.data;
      console.log('✅ [E-INVOICE INTEGRATION] SQL\'e başarıyla kaydedildi:', result);

      // Başarı mesajı göster
      setSuccess(`${currentData.providerType === 'finansbank' ? 'Finansbank' : 'EDM'} ${tt('entegrasyonu başarıyla kaydedildi!', 'integration saved successfully!')}`);
      
      // Verileri yeniden yükle
      await loadAllIntegrations();

      setTimeout(() => {
        setSuccess(null);
      }, 2000);
      
    } catch (err) {
      console.error("❌ [E-INVOICE INTEGRATION] Entegrasyon bilgileri kaydedilirken hata:", err);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // EDM bağlantı testi: yeni gts_einvoice_api'ye gider, kimlik OwnJwt claim'inden,
  // credential SQL'deki kayıttan çözülür (frontend kullanıcı/şifre GÖNDERMEZ).
  const handleEdmTestConnection = async () => {
    setEdmTestResult(null);
    setEdmTesting(true);
    const formatEnvSuffix = (result: any) => {
      if (result?.endpoint) return ` • ${result.endpoint}`;
      if (result?.isProduction === true) return ' • CANLI (PROD)';
      if (result?.isProduction === false) return ' • TEST';
      return '';
    };
    try {
      const response = await einvoiceApi.post(EINVOICE_API.EDM_TEST_CONNECTION, {});
      const result = response.data;
      if (result?.success) {
        setEdmTestResult({
          ok: true,
          message: tt(
            `Bağlantı başarılı (${result.taxNumber || ''}${formatEnvSuffix(result)})`,
            `Connection OK (${result.taxNumber || ''}${formatEnvSuffix(result)})`
          ),
        });
      } else {
        setEdmTestResult({
          ok: false,
          message: `${result?.error || tt('Bağlantı testi başarısız.', 'Connection test failed.')}${formatEnvSuffix(result)}`,
        });
      }
    } catch (err: any) {
      console.error('[EDM] Bağlantı testi hatası:', err);
      const data = err?.response?.data;
      const detail = [data?.errorShort, data?.errorLong || data?.error].filter(Boolean).join(' — ');
      const msg = detail || handleApiError(err);
      setEdmTestResult({ ok: false, message: `${msg}${formatEnvSuffix(data)}` });
    } finally {
      setEdmTesting(false);
    }
  };

  // EDM hesabında kayıtlı fatura serilerini getirir (GetInvoiceSerial).
  // Credential SQL'den çözülür → entegrasyonun önce kaydedilmiş olması gerekir.
  const handleEdmFetchSerials = async () => {
    setEdmSerialsError(null);
    setEdmSerialsLoading(true);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.EDM_SERIALS, {});
      const result = response.data;
      if (result?.success) {
        setEdmSerials(result.serials || []);
        if (!result.serials?.length) {
          setEdmSerialsError(tt(
            'EDM hesabınızda bu yıl için kayıtlı seri bulunamadı. Seriyi EDM portalından tanımlayın veya elle girin.',
            'No registered serials found for this year in your EDM account. Define one in the EDM portal or enter it manually.'
          ));
        }
      } else {
        setEdmSerials(null);
        setEdmSerialsError(result?.error || tt('Seriler getirilemedi.', 'Could not fetch serials.'));
      }
    } catch (err) {
      console.error('[EDM] Seri listeleme hatası:', err);
      setEdmSerials(null);
      setEdmSerialsError(handleApiError(err));
    } finally {
      setEdmSerialsLoading(false);
    }
  };

  // Listeden seçilen seriyi ilgili alana yazar (kaydetmek için yine Kaydet gerekir)
  const handlePickSerial = (item: EdmSerialItem) => {
    setEdmData(prev => item.isArchive
      ? { ...prev, eArsivSerialPrefix: item.code }
      : { ...prev, eInvoiceSerialPrefix: item.code });
  };

  // Hesabın GİB'de kayıtlı GB (gönderici birim) etiketlerini getirir (CheckUser).
  // Yanlış etiketle gönderim EDM "GİB etiketi uyuşmuyor" hatasıyla reddedilir;
  // kullanıcı buradan hesabına ait doğru etiketi seçip kaydedebilir.
  const handleEdmFetchGbAliases = async () => {
    setEdmGbAliasesError(null);
    setEdmGbAliasesLoading(true);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.EDM_GB_ALIASES, {});
      const result = response.data;
      if (result?.success) {
        setEdmGbAliases(result.aliases || []);
        if (!result.aliases?.length) {
          setEdmGbAliasesError(tt(
            'Hesabın VKN\'sinde GİB\'de kayıtlı GB etiketi bulunamadı. Etiketi elle girebilirsiniz.',
            'No GB alias registered at GIB for the account tax number. You can enter it manually.'
          ));
        }
      } else {
        setEdmGbAliases(null);
        setEdmGbAliasesError(result?.error || tt('GB etiketleri getirilemedi.', 'Could not fetch GB aliases.'));
      }
    } catch (err) {
      console.error('[EDM] GB etiketi listeleme hatası:', err);
      setEdmGbAliases(null);
      setEdmGbAliasesError(handleApiError(err));
    } finally {
      setEdmGbAliasesLoading(false);
    }
  };

  // Listeden seçilen GB etiketini alana yazar (kaydetmek için yine Kaydet gerekir)
  const handlePickGbAlias = (item: EdmGbAliasItem) => {
    setEdmData(prev => ({ ...prev, senderAlias: item.alias }));
  };

  // Şirketin banka hesaplarını yükler (faturada gösterilecek hesap seçimi için).
  const loadBankAccounts = async () => {
    if (bankAccounts !== null || bankAccountsLoading) return;
    setBankAccountsLoading(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      const raw = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setBankAccounts(raw.map((b: any) => ({
        id: b.id ?? b.Id,
        bankName: b.bankName ?? b.BankName ?? '',
        branchName: b.branchName ?? b.BranchName ?? '',
        iban: b.iban ?? b.IBAN ?? b.Iban ?? '',
      })));
    } catch (err) {
      console.error('[E-FATURA] Banka hesapları yüklenemedi:', err);
      setBankAccounts([]);
    } finally {
      setBankAccountsLoading(false);
    }
  };

  // "[3,7]" biçimindeki JSON seçimini güvenle diziye çevirir.
  const parseSelectedBankIds = (json?: string): number[] => {
    if (!json) return [];
    try {
      const arr = JSON.parse(json);
      return Array.isArray(arr) ? arr.filter((n: any) => Number.isInteger(n)) : [];
    } catch {
      return [];
    }
  };

  // Banka hesabı seçimini aç/kapat (kaydetmek için yine Kaydet gerekir).
  const toggleBankAccount = (id: number) => {
    setEdmData(prev => {
      const cur = parseSelectedBankIds(prev.invoiceBankAccountIds);
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
      return { ...prev, invoiceBankAccountIds: next.length ? JSON.stringify(next) : '' };
    });
  };

  // EDM mükellef sorgu: VKN/TCKN GİB'de e-fatura kullanıcısı mı? (e-fatura/e-arşiv kararı)
  const handleEdmMukellefSorgula = async () => {
    const vkn = edmQueryVkn.trim();
    if (!vkn) {
      setEdmQueryResult({ ok: false, message: tt('VKN/TCKN girin.', 'Enter a Tax/ID number.') });
      return;
    }
    setEdmQueryResult(null);
    setEdmQuerying(true);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn: vkn });
      const result = response.data;
      const users = Array.isArray(result?.users) ? result.users : [];
      if (result?.found && result?.isEInvoiceUser) {
        setEdmQueryResult({
          ok: true,
          isEInvoiceUser: true,
          title: users[0]?.title,
          users,
          message: tt(
            `E-Fatura mükellefi ✓ (${result.count} posta kutusu) → E-FATURA gönderilir`,
            `Registered e-invoice user ✓ (${result.count} mailbox) → will be sent as E-INVOICE`
          ),
        });
      } else {
        setEdmQueryResult({
          ok: true,
          isEInvoiceUser: false,
          message: tt(
            'Mükellef değil → E-ARŞIV gönderilir',
            'Not registered → will be sent as E-ARCHIVE'
          ),
        });
      }
    } catch (err) {
      console.error('[EDM] Mükellef sorgu hatası:', err);
      setEdmQueryResult({ ok: false, message: handleApiError(err) });
    } finally {
      setEdmQuerying(false);
    }
  };

  const handleDelete = async (provider: 'finansbank' | 'edm') => {
    const data = provider === 'finansbank' ? finansbankData : edmData;
    
    if (!data.id) {
      setError(tt('Bu entegrasyon henüz kaydedilmemiş.', 'This integration is not saved yet.'));
      return;
    }

    if (!window.confirm(`${provider === 'finansbank' ? 'Finansbank' : 'EDM'} ${tt('entegrasyonunu silmek istediğinizden emin misiniz?', 'integration. Are you sure you want to delete it?')}`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await accountingApi.post(ACCOUNTING_API.EINVOICE_INTEGRATION_DELETE, {
        integrationId: data.id,
        deletedBy: userDetails?.email || 'Unknown'
      });

      setSuccess(`${provider === 'finansbank' ? 'Finansbank' : 'EDM'} ${tt('entegrasyonu başarıyla silindi.', 'integration deleted successfully.')}`);
      
      // State'i sıfırla
      if (provider === 'finansbank') {
        setFinansbankData({ ...initialIntegrationData, providerType: 'finansbank', companyName: finansbankData.companyName, taxNumber: finansbankData.taxNumber });
        setFinansbankOriginalPassword('');
      } else {
        setEdmData({ ...initialIntegrationData, providerType: 'edm', companyName: edmData.companyName, taxNumber: edmData.taxNumber });
        setEdmOriginalPassword('');
      }

      // Verileri yeniden yükle
      await loadAllIntegrations();

      setTimeout(() => {
        setSuccess(null);
      }, 2000);

    } catch (err) {
      console.error('❌ [E-INVOICE INTEGRATION] Silme hatası:', err);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    setError(null);
    setSuccess(null);
    setShowPasswordFinansbank(false);
    setShowPasswordEdm(false);
    setShowPavoPayPassword(false);
    setPavoPayUsername('');
    setPavoPayPassword('');
    setPavoPayBranchId('');
    setPavoPayBranchName('');
    setEdmTestResult(null);
    setEdmQueryVkn('');
    setEdmQueryResult(null);
    setEdmSerials(null);
    setEdmSerialsError(null);
    setActiveTab(0);
    onClose();
  };

  const renderIntegrationForm = (provider: 'finansbank' | 'edm') => {
    const data = provider === 'finansbank' ? finansbankData : edmData;
    const showPassword = provider === 'finansbank' ? showPasswordFinansbank : showPasswordEdm;
    const hasExistingIntegration = !!data.id;
    
    // ✅ Karşı provider'ın aktif olup olmadığını kontrol et
    const otherProvider = provider === 'finansbank' ? edmData : finansbankData;
    const isOtherProviderActive = otherProvider.isActive;
    const canEdit = !isOtherProviderActive; // Karşı provider aktif değilse düzenlenebilir

    return (
      <Grid container spacing={3}>
        {/* Uyarı: Diğer entegrasyon aktif */}
        {isOtherProviderActive && (
          <Grid item xs={12}>
            <Alert severity="warning" icon={<CancelIcon />}>
              <Typography variant="body2" fontWeight="bold">
                🚫 {provider === 'finansbank' ? 'EDM' : 'Finansbank'} {tt('entegrasyonu aktif olduğu sürece bu entegrasyonu düzenleyemezsiniz.', 'integration is active, so you cannot edit this integration.')}
              </Typography>
              <Typography variant="caption">
                {tt('Önce aktif entegrasyonu pasif yapın, ardından bu entegrasyonu düzenleyebilir veya aktif edebilirsiniz.', 'First deactivate the active integration, then you can edit or activate this one.')}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Durum ve Aktif/Pasif Switch - Her zaman göster */}
        <Grid item xs={12}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              backgroundColor: !hasExistingIntegration 
                ? '#f5f5f5' 
                : data.isActive 
                  ? '#e8f5e9' 
                  : '#fff3e0' 
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!hasExistingIntegration ? (
                  <CancelIcon color="disabled" fontSize="large" />
                ) : data.isActive ? (
                  <CheckCircleIcon color="success" fontSize="large" />
                ) : (
                  <CancelIcon color="warning" fontSize="large" />
                )}
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {!hasExistingIntegration 
                      ? tt('Entegrasyon Henüz Kaydedilmedi', 'Integration Not Saved Yet') 
                      : data.isActive 
                        ? tt('Aktif Entegrasyon', 'Active Integration') 
                        : tt('Pasif Entegrasyon', 'Passive Integration')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {!hasExistingIntegration 
                      ? tt('Önce entegrasyon bilgilerini kaydedip sonra aktif edebilirsiniz.', 'You can activate it after saving integration details.') 
                      : data.isActive 
                        ? tt('Bu entegrasyon şu anda e-fatura işlemleri için kullanılıyor.', 'This integration is currently used for e-invoice operations.') 
                        : tt('Bu entegrasyonu aktif etmek için switch\'i açın.', 'Enable the switch to activate this integration.')}
                  </Typography>
                </Box>
              </Box>
              <Tooltip 
                title={
                  !hasExistingIntegration 
                    ? tt('Önce bilgileri kaydedin', 'Save details first') 
                    : isOtherProviderActive 
                      ? tt('Diğer entegrasyon aktif olduğu için bu entegrasyon aktif edilemez', 'This integration cannot be activated while the other one is active')
                      : ''
                } 
                arrow
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={data.isActive}
                        onChange={() => handleToggleActive(provider)}
                        disabled={saving || !hasExistingIntegration || isOtherProviderActive}
                        color="success"
                      />
                    }
                    label={data.isActive ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')}
                  />
                </span>
              </Tooltip>
            </Box>
          </Paper>
        </Grid>

        {/* Firma Bilgileri Bölümü */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" color="primary" fontWeight="bold">
                {tt('Firma Bilgileri', 'Company Information')}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={tt('Ticari Ünvan', 'Company Title')}
                  name="companyName"
                  value={data.companyName}
                  onChange={handleChange(provider)}
                  variant="outlined"
                  error={formErrors.companyName}
                  disabled={isOtherProviderActive}
                  helperText={
                    isOtherProviderActive
                      ? tt("Diğer entegrasyon aktif olduğu için değiştirilemez", "Cannot be changed while other integration is active")
                      : formErrors.companyName 
                        ? tt("Ticari ünvan zorunludur.", "Company title is required.") 
                        : companyDetailsLoaded 
                          ? tt("Firma bilgilerinden otomatik alındı", "Auto-filled from company information") 
                          : ""
                  }
                  InputProps={{
                    readOnly: companyDetailsLoaded,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: companyDetailsLoaded ? '#f5f5f5' : 'transparent'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={tt('Vergi Numarası', 'Tax Number')}
                  name="taxNumber"
                  value={data.taxNumber}
                  onChange={handleChange(provider)}
                  variant="outlined"
                  error={formErrors.taxNumber}
                  disabled={isOtherProviderActive}
                  helperText={
                    isOtherProviderActive
                      ? tt("Diğer entegrasyon aktif olduğu için değiştirilemez", "Cannot be changed while other integration is active")
                      : formErrors.taxNumber 
                        ? tt("Vergi numarası zorunludur.", "Tax number is required.") 
                        : companyDetailsLoaded 
                          ? tt("Firma bilgilerinden otomatik alındı", "Auto-filled from company information") 
                          : ""
                  }
                  InputProps={{
                    readOnly: companyDetailsLoaded,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: companyDetailsLoaded ? '#f5f5f5' : 'transparent'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Entegrasyon Bilgileri Bölümü */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fff3e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon color="warning" />
              <Typography variant="h6" color="warning.dark" fontWeight="bold">
                {provider === 'finansbank' ? 'Finansbank' : 'EDM'} {tt('Entegrasyon Bilgileri', 'Integration Information')}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={tt('Kullanıcı Adı', 'Username')}
                  name="username"
                  value={data.username}
                  onChange={handleChange(provider)}
                  variant="outlined"
                  error={formErrors.username}
                  disabled={isOtherProviderActive}
                  helperText={
                    isOtherProviderActive
                      ? tt("Diğer entegrasyon aktif olduğu için değiştirilemez", "Cannot be changed while other integration is active")
                      : formErrors.username 
                        ? tt("Kullanıcı adı zorunludur.", "Username is required.") 
                        : hasExistingIntegration 
                          ? tt("Mevcut entegrasyon bilgisi yüklendi", "Existing integration details loaded") 
                          : tt("Entegratörden aldığınız kullanıcı adı", "Username received from integrator")
                  }
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={tt('Şifre', 'Password')}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={data.password}
                  onChange={handleChange(provider)}
                  variant="outlined"
                  error={formErrors.password}
                  disabled={isOtherProviderActive}
                  helperText={
                    isOtherProviderActive
                      ? tt("Diğer entegrasyon aktif olduğu için değiştirilemez", "Cannot be changed while other integration is active")
                      : formErrors.password 
                        ? tt("Şifre zorunludur.", "Password is required.") 
                        : hasExistingIntegration 
                          ? tt("Mevcut şifre korunuyor. Değiştirmek için yeni şifre girin.", "Current password is preserved. Enter a new one to change it.") 
                          : tt("Entegratörden aldığınız şifre", "Password received from integrator")
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility(provider)}
                          edge="end"
                          disabled={isOtherProviderActive}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* EDM Ortam Seçimi (Test / Canlı) */}
        {provider === 'edm' && (
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{ p: 2, backgroundColor: data.isProduction ? '#fff8e1' : '#f1f8e9' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUserIcon color={data.isProduction ? 'warning' : 'success'} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color={data.isProduction ? 'warning.dark' : 'success.dark'}>
                      {tt('Ortam', 'Environment')}: {data.isProduction ? tt('CANLI (Production)', 'LIVE (Production)') : tt('TEST', 'TEST')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.isProduction
                        ? tt('Faturalar GERÇEK olarak GİB\'e gönderilir. Kullanıcı adı/şifre ve seriler CANLI hesabınkiler olmalıdır.', 'Invoices are sent to GİB for REAL. Credentials and serials must belong to the LIVE account.')
                        : tt('Faturalar EDM test ortamına gönderilir; GİB\'e gerçek belge gitmez.', 'Invoices go to the EDM test environment; no real document is sent to GİB.')}
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.isProduction === true}
                      onChange={(e) => setEdmData(prev => ({ ...prev, isProduction: e.target.checked }))}
                      disabled={isOtherProviderActive}
                      color="warning"
                    />
                  }
                  label={data.isProduction ? tt('Canlı', 'Live') : tt('Test', 'Test')}
                />
              </Box>
              {data.isProduction && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  <Typography variant="body2">
                    {tt(
                      'Canlıya geçmeden önce kontrol listesi: (1) Kullanıcı adı/şifre CANLI EDM hesabına ait olmalı, (2) "Serileri Getir" ile canlı hesaptaki serileri çekip seçin, (3) Kaydet\'e basın ve "Bağlantıyı Test Et" ile doğrulayın. Bu seçim kaydedilene kadar geçerli olmaz.',
                      'Pre-live checklist: (1) Credentials must belong to the LIVE EDM account, (2) fetch and select serials from the live account, (3) Save and verify with "Test Connection". This selection takes effect only after saving.'
                    )}
                  </Typography>
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {/* EDM Fatura Serileri (e-fatura / e-arşiv önekleri) */}
        {provider === 'edm' && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f3f8ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="info" />
                  <Typography variant="h6" color="info.dark" fontWeight="bold">
                    {tt('Fatura Serileri', 'Invoice Serials')}
                  </Typography>
                </Box>
                <Tooltip
                  title={!hasExistingIntegration
                    ? tt('Serileri getirmek için önce entegrasyonu kaydedin', 'Save the integration first to fetch serials')
                    : ''}
                  arrow
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={handleEdmFetchSerials}
                      disabled={edmSerialsLoading || !hasExistingIntegration || isOtherProviderActive}
                      startIcon={edmSerialsLoading ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />}
                    >
                      {edmSerialsLoading ? tt('Getiriliyor...', 'Fetching...') : tt("EDM'den Serileri Getir", 'Fetch Serials from EDM')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tt('E-Fatura Seri Öneki', 'E-Invoice Serial Prefix')}
                    name="eInvoiceSerialPrefix"
                    value={data.eInvoiceSerialPrefix || ''}
                    onChange={handleChange(provider)}
                    disabled={isOtherProviderActive}
                    inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                    placeholder={tt('Örn: ABC', 'e.g. ABC')}
                    helperText={tt(
                      'EDM hesabınızda kayıtlı e-fatura serisi. Boş bırakılırsa gönderimde otomatik seçilir.',
                      'E-invoice serial registered in your EDM account. Auto-resolved on send if left empty.'
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={tt('E-Arşiv Seri Öneki', 'E-Archive Serial Prefix')}
                    name="eArsivSerialPrefix"
                    value={data.eArsivSerialPrefix || ''}
                    onChange={handleChange(provider)}
                    disabled={isOtherProviderActive}
                    inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                    placeholder={tt('Örn: ABD', 'e.g. ABD')}
                    helperText={tt(
                      'E-arşiv faturalar için ayrı seri (varsa). Boş bırakılırsa otomatik seçilir.',
                      'Separate serial for e-archive invoices (if any). Auto-resolved if left empty.'
                    )}
                  />
                </Grid>
              </Grid>

              {edmSerialsError && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>{edmSerialsError}</Alert>
              )}

              {edmSerials && edmSerials.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {tt('Hesapta kayıtlı seriler — seçmek için tıklayın:', 'Serials registered in the account — click to select:')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                    {edmSerials.map((s, idx) => (
                      <Paper
                        key={`${s.code}-${idx}`}
                        variant="outlined"
                        onClick={() => handlePickSerial(s)}
                        sx={{
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <Chip
                          label={s.isArchive ? tt('E-Arşiv', 'E-Archive') : tt('E-Fatura', 'E-Invoice')}
                          size="small"
                          color={s.isArchive ? 'secondary' : 'primary'}
                          sx={{ minWidth: 76 }}
                        />
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {s.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {s.year}{s.sendType ? ` • ${s.sendType}` : ''}
                          {s.lastSerialUsed !== undefined && s.lastSerialUsed !== null && s.lastSerialUsed !== ''
                            ? ` • ${tt('Son no', 'Last no')}: ${s.lastSerialUsed}`
                            : ''}
                        </Typography>
                        <Typography variant="caption" color="info.main">
                          {tt('Seç', 'Select')}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {tt('Seçim yaptıktan sonra Kaydet ile kalıcılaştırmayı unutmayın.', 'Remember to persist your selection with Save.')}
                  </Typography>
                </Box>
              )}

              {/* Gönderici Birim (GB) Etiketi — yanlış etiket "GİB etiketi uyuşmuyor" hatasına yol açar */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" color="info.dark" fontWeight="bold">
                  {tt('Gönderici Birim (GB) Etiketi', 'Sender Unit (GB) Alias')}
                </Typography>
                <Tooltip
                  title={!hasExistingIntegration
                    ? tt('Etiketleri getirmek için önce entegrasyonu kaydedin', 'Save the integration first to fetch aliases')
                    : ''}
                  arrow
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={handleEdmFetchGbAliases}
                      disabled={edmGbAliasesLoading || !hasExistingIntegration || isOtherProviderActive}
                      startIcon={edmGbAliasesLoading ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />}
                    >
                      {edmGbAliasesLoading ? tt('Getiriliyor...', 'Fetching...') : tt("EDM'den Etiketleri Getir", 'Fetch Aliases from EDM')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <TextField
                fullWidth
                size="small"
                label={tt('GB Etiketi (Gönderici Birim)', 'GB Alias (Sender Unit)')}
                name="senderAlias"
                value={data.senderAlias || ''}
                onChange={handleChange(provider)}
                disabled={isOtherProviderActive}
                inputProps={{ maxLength: 100 }}
                placeholder={tt('Örn: urn:mail:defaultgb@sirketiniz.com.tr', 'e.g. urn:mail:defaultgb@yourcompany.com')}
                helperText={tt(
                  'Faturalar bu etiketten gönderilir; oturum açtığınız EDM hesabının GB etiketiyle birebir aynı olmalıdır. Boş bırakılırsa gönderimde otomatik çözülür.',
                  'Invoices are sent from this alias; it must exactly match the GB alias of your EDM account. Auto-resolved on send if left empty.'
                )}
              />

              {edmGbAliasesError && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>{edmGbAliasesError}</Alert>
              )}

              {edmGbAliases && edmGbAliases.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {tt('Hesabın VKN\'sinde kayıtlı GB etiketleri — seçmek için tıklayın:', 'GB aliases registered for the account tax number — click to select:')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                    {edmGbAliases.map((a, idx) => (
                      <Paper
                        key={`${a.alias}-${idx}`}
                        variant="outlined"
                        onClick={() => handlePickGbAlias(a)}
                        sx={{
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <Chip
                          label={a.isActive ? tt('AKTİF', 'ACTIVE') : tt('PASİF', 'INACTIVE')}
                          size="small"
                          color={a.isActive ? 'success' : 'default'}
                          sx={{ minWidth: 64 }}
                        />
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.alias}
                        </Typography>
                        <Typography variant="caption" color="info.main">
                          {tt('Seç', 'Select')}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {tt('Seçim yaptıktan sonra Kaydet ile kalıcılaştırmayı unutmayın.', 'Remember to persist your selection with Save.')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Faturada gösterilecek banka hesapları (UBL PaymentMeans + tasarım şablonundaki banka kutusu) */}
        {provider === 'edm' && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.04)' }}>
              <Typography variant="subtitle2" color="success.dark" fontWeight="bold" sx={{ mb: 0.5 }}>
                {tt('Faturada Gösterilecek Banka Hesapları', 'Bank Accounts Shown on Invoice')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {tt(
                  'Seçilen hesapların IBAN bilgileri e-faturaya yazılır ve fatura tasarımındaki "Banka Hesap Bilgileri" kutusunda görünür. Hiçbiri seçilmezse banka kutusu gösterilmez.',
                  'IBANs of the selected accounts are written into the e-invoice and shown in the "Bank Account Details" box of the invoice design. If none is selected, the bank box is hidden.'
                )}
              </Typography>

              {bankAccountsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">{tt('Banka hesapları yükleniyor...', 'Loading bank accounts...')}</Typography>
                </Box>
              )}

              {!bankAccountsLoading && (bankAccounts?.length ?? 0) === 0 && (
                <Alert severity="info">
                  {tt(
                    'Kayıtlı banka hesabı bulunamadı. Banka hesaplarınızı Banka modülünden ekleyebilirsiniz.',
                    'No bank accounts found. You can add bank accounts from the Bank module.'
                  )}
                </Alert>
              )}

              {!bankAccountsLoading && (bankAccounts?.length ?? 0) > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 220, overflow: 'auto' }}>
                  {bankAccounts!.map((b) => {
                    const selected = parseSelectedBankIds(data.invoiceBankAccountIds).includes(b.id);
                    const hasIban = !!(b.iban && b.iban.trim());
                    return (
                      <Paper
                        key={b.id}
                        variant="outlined"
                        onClick={() => hasIban && !isOtherProviderActive && toggleBankAccount(b.id)}
                        sx={{
                          p: 0.75,
                          pl: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: hasIban && !isOtherProviderActive ? 'pointer' : 'default',
                          opacity: hasIban ? 1 : 0.55,
                          border: selected ? '1px solid' : undefined,
                          borderColor: selected ? 'success.main' : undefined,
                          '&:hover': hasIban && !isOtherProviderActive ? { backgroundColor: 'action.hover' } : undefined,
                        }}
                      >
                        <Checkbox
                          size="small"
                          color="success"
                          checked={selected}
                          disabled={!hasIban || isOtherProviderActive}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleBankAccount(b.id)}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={selected ? 'bold' : 'normal'} noWrap>
                            {b.bankName}{b.branchName ? ` — ${b.branchName}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
                            {hasIban ? b.iban : tt('IBAN tanımlı değil (seçilemez)', 'No IBAN defined (not selectable)')}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {tt('Seçim yaptıktan sonra Kaydet ile kalıcılaştırmayı unutmayın.', 'Remember to persist your selection with Save.')}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* EDM Bağlantı Testi (yeni servis) */}
        {provider === 'edm' && hasExistingIntegration && (
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              startIcon={edmTesting ? <CircularProgress size={18} color="inherit" /> : <VerifiedUserIcon />}
              onClick={handleEdmTestConnection}
              disabled={edmTesting || isOtherProviderActive}
            >
              {edmTesting ? tt('Test ediliyor...', 'Testing...') : tt('Bağlantıyı Test Et', 'Test Connection')}
            </Button>
            {edmTestResult && (
              <Alert severity={edmTestResult.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                {edmTestResult.message}
              </Alert>
            )}

            {/* Mükellef sorgu (CheckUser) */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label={tt('VKN / TCKN sorgula', 'Query Tax/ID number')}
                value={edmQueryVkn}
                onChange={(e) => setEdmQueryVkn(e.target.value.replace(/\D/g, ''))}
                placeholder="11111111111"
                inputProps={{ maxLength: 11 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleEdmMukellefSorgula(); }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleEdmMukellefSorgula}
                disabled={edmQuerying || isOtherProviderActive}
                startIcon={edmQuerying ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {edmQuerying ? tt('Sorgulanıyor...', 'Querying...') : tt('Sorgula', 'Query')}
              </Button>
            </Box>
            {edmQueryResult && (
              <Alert severity={edmQueryResult.ok ? (edmQueryResult.isEInvoiceUser ? 'success' : 'info') : 'error'} sx={{ mt: 1 }}>
                {edmQueryResult.message}
              </Alert>
            )}

            {/* Mükellef detayları */}
            {edmQueryResult?.isEInvoiceUser && (edmQueryResult.users?.length ?? 0) > 0 && (
              <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
                {edmQueryResult.title && (
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {edmQueryResult.title}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {tt('Posta Kutuları / Etiketler', 'Mailboxes / Aliases')} ({edmQueryResult.users!.length})
                </Typography>
                <Box sx={{ maxHeight: 220, overflow: 'auto', mt: 0.5 }}>
                  {edmQueryResult.users!.map((u, idx) => (
                    <Box
                      key={`${u.alias}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        borderBottom: idx < edmQueryResult.users!.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {u.unit && (
                        <Chip
                          label={u.unit}
                          size="small"
                          color={u.unit.toUpperCase().includes('PK') ? 'primary' : 'default'}
                          sx={{ minWidth: 48 }}
                        />
                      )}
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                        {u.alias}
                      </Typography>
                      {u.documentType && (
                        <Typography variant="caption" color="text.secondary">
                          {u.documentType}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {tt('PK = Posta Kutusu (alıcı adresi), GB = Gönderici Birim', 'PK = Mailbox (receiver), GB = Sender Unit')}
                </Typography>
              </Paper>
            )}
          </Grid>
        )}

        {/* Silme Butonu */}
        {hasExistingIntegration && (
          <Grid item xs={12}>
            <Tooltip 
              title={isOtherProviderActive ? tt('Diğer entegrasyon aktif olduğu için silinemez', 'Cannot be deleted while other integration is active') : ''} 
              arrow
            >
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(provider)}
                  disabled={saving || isOtherProviderActive}
                >
                  {tt('Bu Entegrasyonu Sil', 'Delete This Integration')}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}

        {/* Güvenlik Uyarısı */}
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>{tt('Güvenlik Notu', 'Security Note')}:</strong> {tt('Entegrasyon bilgileriniz şifrelenmiş olarak saklanacaktır. Bu bilgiler sadece e-fatura işlemleri için kullanılacak ve üçüncü taraflarla paylaşılmayacaktır.', 'Your integration details will be stored encrypted. This information will only be used for e-invoice operations and will not be shared with third parties.')}
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {tt('E-Fatura Entegrasyon Ayarları', 'E-Invoice Integration Settings')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        {!loading && (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    py: 2
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="inherit">Finansbank</Typography>
                      {finansbankData.isActive && <Chip label={tt('Aktif', 'Active')} size="small" color="success" />}
                      {finansbankData.id && !finansbankData.isActive && <Chip label={tt('Pasif', 'Passive')} size="small" color="default" />}
                    </Box>
                  }
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="inherit">EDM</Typography>
                      {edmData.isActive && <Chip label={tt('Aktif', 'Active')} size="small" color="success" />}
                      {edmData.id && !edmData.isActive && <Chip label={tt('Pasif', 'Passive')} size="small" color="default" />}
                    </Box>
                  }
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="inherit">PavoPay</Typography>
                      {pavoPayIntegrations.length > 0 && (
                        <Chip label={pavoPayIntegrations.length} size="small" color="info" />
                      )}
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && renderIntegrationForm('finansbank')}
              {activeTab === 1 && renderIntegrationForm('edm')}
              {activeTab === 2 && (
                <Grid container spacing={3}>
                  {/* Yeni PavoPay Entegrasyonu Ekleme Formu */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SecurityIcon color="info" />
                        <Typography variant="h6" color="info.dark" fontWeight="bold">
                          {tt('Yeni PavoPay Entegrasyonu Ekle', 'Add New PavoPay Integration')}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{tt('Şube (Opsiyonel)', 'Branch (Optional)')}</InputLabel>
                            <Select
                              value={pavoPayBranchId}
                              label={tt('Şube (Opsiyonel)', 'Branch (Optional)')}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPavoPayBranchId(val);
                                if (val && val !== 'null') {
                                  const found = branches.find(b => b.id === val);
                                  setPavoPayBranchName(found?.name || '');
                                } else {
                                  setPavoPayBranchName('');
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>{tt('Şube seçilmedi', 'No branch selected')}</em>
                              </MenuItem>
                              {branches.filter(b => b.id !== 'null').map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                  {branch.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            required
                            label={tt('Kullanıcı Adı', 'Username')}
                            value={pavoPayUsername}
                            onChange={(e) => setPavoPayUsername(e.target.value)}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            required
                            label={tt('Şifre', 'Password')}
                            type={showPavoPayPassword ? 'text' : 'password'}
                            value={pavoPayPassword}
                            onChange={(e) => setPavoPayPassword(e.target.value)}
                            variant="outlined"
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPavoPayPassword(!showPavoPayPassword)}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPavoPayPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="info"
                            onClick={handlePavoPaySave}
                            disabled={pavoPaySaving}
                            startIcon={pavoPaySaving ? <CircularProgress size={16} color="inherit" /> : <SecurityIcon />}
                          >
                            {pavoPaySaving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Mevcut PavoPay Entegrasyonları Listesi */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {tt('Kayıtlı Entegrasyonlar', 'Saved Integrations')}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          endIcon={<OpenInNewIcon />}
                          onClick={() => {
                            handleClose();
                            navigate('/e-fatura/pavopay-entegrasyonlar');
                          }}
                        >
                          {tt('Tüm Entegrasyonları Yönet', 'Manage All Integrations')}
                        </Button>
                      </Box>

                      {pavoPayLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : pavoPayIntegrations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          {tt('Henüz PavoPay entegrasyonu eklenmemiş.', 'No PavoPay integration has been added yet.')}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {pavoPayIntegrations.map((item: any) => (
                            <Paper
                              key={item.id || item.Id}
                              variant="outlined"
                              sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {(item.isActive ?? item.IsActive) ? (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="disabled" fontSize="small" />
                                )}
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {item.username || item.Username}
                                    {(item.branchName || item.BranchName) && (
                                      <Chip label={item.branchName || item.BranchName} size="small" sx={{ ml: 1 }} />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {(item.lastAuthDate || item.LastAuthDate)
                                      ? `${tt('Son auth', 'Last auth')}: ${new Date(item.lastAuthDate || item.LastAuthDate).toLocaleString(localeCode)}`
                                      : tt('Henüz test edilmedi', 'Not tested yet')}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title={tt('Bağlantı Test Et', 'Test Connection')}>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handlePavoPayAuthTest(item.id || item.Id)}
                                    disabled={pavoPayTesting}
                                  >
                                    {pavoPayTesting ? <CircularProgress size={16} /> : <VerifiedUserIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={tt('Sil', 'Delete')}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handlePavoPayDelete(item.id || item.Id)}
                                    disabled={pavoPaySaving}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        {tt('PavoPay (Aktif Bank e-Dönüşüm) entegrasyonu şube bazlı çalışır. Her şube için ayrı bir entegrasyon ekleyebilirsiniz.', 'PavoPay (Aktif Bank e-Transformation) integration works branch-based. You can add a separate integration for each branch.')}
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, backgroundColor: 'grey.50' }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {(finansbankData.id || edmData.id) ? tt('Kapat', 'Close') : tt('İptal', 'Cancel')}
        </Button>
        {activeTab !== 2 && (
          <Tooltip 
            title={
              (activeTab === 0 && edmData.isActive) 
                ? tt('EDM aktif olduğu için Finansbank kaydedilemez', 'Finansbank cannot be saved while EDM is active') 
                : (activeTab === 1 && finansbankData.isActive)
                  ? tt('Finansbank aktif olduğu için EDM kaydedilemez', 'EDM cannot be saved while Finansbank is active')
                  : ''
            } 
            arrow
          >
            <span>
              <Button 
                onClick={handleSave} 
                variant="contained" 
                color="primary" 
                disabled={
                  saving || 
                  loading || 
                  (activeTab === 0 && edmData.isActive) || 
                  (activeTab === 1 && finansbankData.isActive)
                }
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
              >
                {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceIntegrationModal;
